import { httpClient } from '@/shell';
import type {
  SupervisorOrganization,
  SupervisorOrgDetail,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  AssumeOrgResponse,
  SupervisorListItem,
  CreateSupervisorRequest,
  SupervisorAccess,
  UpdateSupervisorAccessRequest,
  SsoOrgDetail,
  SsoOrgUser,
  SsoInstance,
  SsoUserLookupResponse,
  OrgMember,
  OrgMemberListResponse,
  GrantOrgMemberRoleRequest,
  DeactivateSsoInstanceResponse,
  FeatureFlag,
  FeatureFlagUpdate,
  FeatureFlagScheduleInput,
  StuckCharge,
  ResolveStuckChargeRequest,
  StuckRefund,
  ResolveStuckRefundRequest,
  StrandedVerificationHold,
  RetryHoldReleaseResult,
} from '@neon-one/auctions-types';

interface SupervisorConfig {
  bidderUrlTemplate: string | null;
}

export const supervisorApi = {
  getConfig: () =>
    httpClient().get<SupervisorConfig>('/admin/supervisor/config'),

  getAccess: () =>
    httpClient().get<SupervisorAccess>('/admin/supervisor/me'),

  listOrgs: () =>
    httpClient().get<SupervisorOrganization[]>('/admin/supervisor/organizations'),

  getOrg: (salesforceId: string) =>
    httpClient().get<SupervisorOrgDetail>(`/admin/supervisor/organizations/${encodeURIComponent(salesforceId)}`),

  updateOrg: (salesforceId: string, data: UpdateOrganizationRequest) =>
    httpClient().patch<SupervisorOrgDetail>(`/admin/supervisor/organizations/${encodeURIComponent(salesforceId)}`, data),

  createOrg: (data: CreateOrganizationRequest) =>
    httpClient().post<SupervisorOrganization>('/admin/supervisor/organizations', data),

  listOrgMembers: (salesforceId: string) =>
    httpClient().get<OrgMemberListResponse>(
      `/admin/supervisor/organizations/${encodeURIComponent(salesforceId)}/users`,
    ),

  grantOrgMember: (salesforceId: string, data: GrantOrgMemberRoleRequest) =>
    httpClient().post<OrgMember>(
      `/admin/supervisor/organizations/${encodeURIComponent(salesforceId)}/users`,
      data,
    ),

  revokeOrgMember: (salesforceId: string, userId: number) =>
    httpClient().delete(
      `/admin/supervisor/organizations/${encodeURIComponent(salesforceId)}/users/${userId}`,
    ),

  deactivateSsoInstance: (salesforceId: string) =>
    httpClient().post<DeactivateSsoInstanceResponse>(
      `/admin/supervisor/organizations/${encodeURIComponent(salesforceId)}/deactivate-sso-instance`,
      {},
    ),

  assumeOrg: (salesforceId: string) =>
    httpClient().post<AssumeOrgResponse>('/admin/supervisor/assume-org', { salesforceId }),

  listSupervisors: () =>
    httpClient().get<SupervisorListItem[]>('/admin/supervisor/supervisors'),

  addSupervisor: (data: CreateSupervisorRequest) =>
    httpClient().post<{ id: number }>('/admin/supervisor/supervisors', data),

  updateSupervisorAccess: (email: string, data: UpdateSupervisorAccessRequest) =>
    httpClient().patch<SupervisorListItem>(`/admin/supervisor/supervisors/${encodeURIComponent(email)}`, data),

  removeSupervisor: (email: string) =>
    httpClient().delete(`/admin/supervisor/supervisors/${encodeURIComponent(email)}`),

  getSsoOrg: (ssoOrgId: number) =>
    httpClient().get<SsoOrgDetail>(`/admin/supervisor/sso/org/${ssoOrgId}`),

  searchSsoOrg: (params: { salesforceId?: string; ein?: string }) => {
    const query = new URLSearchParams();
    if (params.salesforceId) query.set('salesforceId', params.salesforceId);
    if (params.ein) query.set('ein', params.ein);
    return httpClient().get<SsoOrgDetail>(`/admin/supervisor/sso/org-search?${query}`);
  },

  getSsoOrgUsers: (ssoOrgId: number) =>
    httpClient().get<SsoOrgUser[]>(`/admin/supervisor/sso/org/${ssoOrgId}/users`),

  lookupOrgUser: (ssoOrgId: number, email: string) =>
    httpClient().get<SsoUserLookupResponse>(
      `/admin/supervisor/sso/org/${ssoOrgId}/lookup-user?email=${encodeURIComponent(email)}`,
    ),

  getSsoOrgInstances: (ssoOrgId: number) =>
    httpClient().get<SsoInstance[]>(`/admin/supervisor/sso/org/${ssoOrgId}/instances`),

  // Feature flags — every mutation returns the refreshed full flag list.
  listFeatureFlags: () =>
    httpClient().get<FeatureFlag[]>('/admin/supervisor/feature-flags'),

  updateFeatureFlag: (key: string, data: FeatureFlagUpdate) =>
    httpClient().patch<FeatureFlag[]>(
      `/admin/supervisor/feature-flags/${encodeURIComponent(key)}`,
      data,
    ),

  setFeatureFlagOverride: (key: string, salesforceId: string, enabled: boolean) =>
    httpClient().put<FeatureFlag[]>(
      `/admin/supervisor/feature-flags/${encodeURIComponent(key)}/org-overrides/${encodeURIComponent(salesforceId)}`,
      { enabled },
    ),

  removeFeatureFlagOverride: (key: string, salesforceId: string) =>
    httpClient().delete<FeatureFlag[]>(
      `/admin/supervisor/feature-flags/${encodeURIComponent(key)}/org-overrides/${encodeURIComponent(salesforceId)}`,
    ),

  setFeatureFlagSchedule: (key: string, data: FeatureFlagScheduleInput) =>
    httpClient().put<FeatureFlag[]>(
      `/admin/supervisor/feature-flags/${encodeURIComponent(key)}/schedule`,
      data,
    ),

  clearFeatureFlagSchedule: (key: string) =>
    httpClient().delete<FeatureFlag[]>(
      `/admin/supervisor/feature-flags/${encodeURIComponent(key)}/schedule`,
    ),

  // Stuck charges — cross-org invoices left in `processing`. The list is a
  // bare array (no pagination envelope); resolve returns the updated invoice,
  // which callers ignore in favour of reloading the list.
  listStuckCharges: () =>
    httpClient().get<StuckCharge[]>('/admin/supervisor/stuck-charges'),

  resolveStuckCharge: (invoiceUuid: string, data: ResolveStuckChargeRequest) =>
    httpClient().post(
      `/admin/supervisor/stuck-charges/${encodeURIComponent(invoiceUuid)}/resolve`,
      data,
    ),

  // Stuck refunds — refunds we sent to Neon Pay and never got an id back for.
  // A separate endpoint and wire type from stuck charges, and keyed by
  // transaction id rather than invoice uuid: an invoice can carry several
  // refunds, and which one is stuck is the whole question.
  listStuckRefunds: () =>
    httpClient().get<StuckRefund[]>('/admin/supervisor/stuck-refunds'),

  resolveStuckRefund: (paymentTransactionId: number, data: ResolveStuckRefundRequest) =>
    httpClient().post(
      `/admin/supervisor/stuck-refunds/${paymentTransactionId}/resolve`,
      data,
    ),

  // Stranded card-verification holds — authorizations whose release never
  // landed. A separate endpoint and a separate wire type from stuck charges on
  // purpose: a stuck charge is money a bidder OWES, a stranded hold is money
  // already held that we must GIVE BACK (spec §8.3).
  listStrandedHolds: () =>
    httpClient().get<StrandedVerificationHold[]>('/admin/supervisor/stranded-holds'),

  // No request body — the only action this surface offers is "release the hold
  // you are already showing me". A failed release comes back 200 with the hold
  // still `pending`, not as an error.
  retryHoldRelease: (paymentMethodPublicId: string) =>
    httpClient().post<RetryHoldReleaseResult>(
      `/admin/supervisor/stranded-holds/${encodeURIComponent(paymentMethodPublicId)}/retry-release`,
    ),
};
